import { useEffect, useState, useRef, useCallback } from "react";
import { Grid, useTheme, Theme, Typography, styled, GridProps } from "@mui/material";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { useDispatch } from "react-redux";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import WarningIcon from "@mui/icons-material/Warning";

interface IPairingSummaryWindowProps {
  windowProps: {
    pairingId: string;
    crewId?: string;
    isBidPairing?: boolean;
  };
}

const OCCrewPairingSummary = styled((props: GridProps) => <Grid {...props} />)(({ theme }) => ({
  display: "flex",
  height: "100%",
  backgroundColor: "#E1E8EF",
  "& .summary_row": {
    padding: 15,
    height: "100%",
    overflowY: "auto",
    "& #pairing-summary": {
      height: "100%",
      "& #pairing-summary-table-container": {
        maxHeight: "unset",
        paddingBottom: 15,
      },
    },
  },
}));

/**
 * Render a pairing summary using the pairing and crew info supplied using handlebars.
 * @param props -
 */
export const PairingSummaryWindow = (props: IPairingSummaryWindowProps): JSX.Element => {
  const { pairingId, crewId, isBidPairing } = props.windowProps;
  const dispatch = useDispatch();
  const summaryRef = useRef();
  const [templateHTML, setTemplateHTML] = useState<string | null>(null);

  const getPairingSummaryAction = useCallback(async (): Promise<void> => {
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Loading Pairing Summary",
      successMessage: "Pairing Summary Successfully Loaded",
      errorMessage: "Error: Unable To Load Pairing Summary",
    };
    const template = await AmplifyAPIWrapper.Get<any>(
      `/getPairingTemplate/${pairingId}/user/${crewId}?templatePath=/pairingSummary`,
      notifications
    );
    // console.log("TEMPLATE: ", template?.templateHTML);
    if (template?.templateHTML) setTemplateHTML(template?.templateHTML);
  }, [crewId, dispatch, pairingId]);

  const getBidPairingSummaryAction = useCallback(async (): Promise<void> => {
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Loading Bid Pairing Summary",
      successMessage: "Bid Pairing Summary Successfully Loaded",
      errorMessage: "Error: Unable To Load Bid Pairing Summary",
    };
    const template = await AmplifyAPIWrapper.Get<any>(
      `/bidding/pairing/${pairingId}/template?template=pairingSummary`,
      notifications
    );
    // console.log("TEMPLATE: ", template?.templateHTML);
    if (template?.templateHTML) setTemplateHTML(template?.templateHTML);
  }, [dispatch, pairingId]);

  useEffect(() => {
    if (!isBidPairing) {
      // PAIRING
      getPairingSummaryAction();
    } else {
      // BID PAIRING (snapshot of pairing)
      getBidPairingSummaryAction();
    }
  }, [dispatch, pairingId, crewId, isBidPairing, getPairingSummaryAction, getBidPairingSummaryAction]);

  return (
    <OCCrewPairingSummary container direction="column" wrap="nowrap">
      {templateHTML ? (
        <Grid item className="summary_row" ref={summaryRef}>
          {isBidPairing && (
            <Grid container alignItems="center">
              <WarningIcon color="secondary" fontSize="small" />
              <Typography variant="caption" color="secondary" style={{ margin: 5, fontWeight: 900 }}>
                Bid Pairing Summary - Actual pairing may have changed since snapshot was generated.
              </Typography>
            </Grid>
          )}
          <div style={{ height: "100%" }} dangerouslySetInnerHTML={{ __html: templateHTML }} />
        </Grid>
      ) : (
        <SpinnerProgress loading={true} message={`loading ${isBidPairing ? "bid pairing" : "pairing"} summary`} />
      )}
    </OCCrewPairingSummary>
  );
};
